import type {
  EncodingChannel,
  FormatSpec,
  QueryResult,
  QueryResultColumn,
  ScalarValue,
  Selection,
  TimeGrain,
  VisualizationSpec,
} from '@aibi/contracts';

export interface IndexedColumn {
  readonly column: QueryResultColumn;
  readonly index: number;
}

/** Columnas asignadas a un canal; si el canal esta vacio, recurre al rol indicado. */
export function channelColumns(
  spec: VisualizationSpec,
  result: QueryResult,
  channel: EncodingChannel,
  fallbackRole?: QueryResultColumn['role'],
): IndexedColumn[] {
  const keys = spec.encodings[channel];
  const indexed = result.columns.map((column, index) => ({ column, index }));
  if (keys && keys.length > 0) {
    return keys.flatMap((key) => indexed.filter((entry) => entry.column.key === key));
  }
  return fallbackRole === undefined
    ? []
    : indexed.filter((entry) => entry.column.role === fallbackRole);
}

/** Formato efectivo: el de la visualizacion tiene prioridad sobre el del resultado. */
export const effectiveFormat = (
  spec: VisualizationSpec,
  column: QueryResultColumn,
): FormatSpec | undefined => spec.formatting.fields[column.key] ?? column.format;

const LOCALE = 'es';
const EMPTY = '(vacio)';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function formatPeriod(value: string, grain: TimeGrain | undefined): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return value;
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  switch (grain) {
    case 'year':
      return String(year);
    case 'quarter':
      return `T${String(Math.floor(month / 3) + 1)} ${String(year)}`;
    case 'month':
      return `${MONTHS[month] ?? ''} ${String(year)}`;
    case 'hour':
      return `${String(day)} ${MONTHS[month] ?? ''} ${String(date.getUTCHours()).padStart(2, '0')}:00`;
    case 'week':
    case 'day':
    case undefined:
      return `${String(day)} ${MONTHS[month] ?? ''} ${String(year)}`;
  }
}

/** Formatea un valor para mostrarlo segun su columna y el formato de la visualizacion. */
export function formatValue(
  value: ScalarValue,
  column: QueryResultColumn,
  format: FormatSpec | undefined = column.format,
  options: { readonly compact?: boolean } = {},
): string {
  if (value === null) return EMPTY;
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  if (typeof value === 'string') {
    if (
      column.timeGrain !== undefined ||
      column.dataType === 'date' ||
      column.dataType === 'datetime'
    ) {
      return formatPeriod(value, column.timeGrain);
    }
    return value;
  }
  const compact = options.compact === true;
  switch (format?.type) {
    case 'currency':
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: format.currency,
        maximumFractionDigits: compact ? 1 : (format.decimals ?? 2),
        minimumFractionDigits: compact ? 0 : (format.decimals ?? 2),
        ...(compact || format.compact === true ? { notation: 'compact' } : {}),
      }).format(value);
    case 'percent':
      return new Intl.NumberFormat(LOCALE, {
        style: 'percent',
        maximumFractionDigits: format.decimals ?? 1,
      }).format(value);
    case 'number': {
      const text = new Intl.NumberFormat(LOCALE, {
        maximumFractionDigits: compact ? 1 : (format.decimals ?? 2),
        minimumFractionDigits: compact ? 0 : (format.decimals ?? 0),
        useGrouping: format.thousandsSeparator ?? true,
        ...(compact || format.compact === true ? { notation: 'compact' } : {}),
      }).format(value);
      return `${format.prefix ?? ''}${text}${format.suffix ?? ''}`;
    }
    case 'date':
    case 'text':
    case undefined: {
      // Una dimension numerica suele ser un codigo (periodo 202609, clave, ano): sin miles ni compactar.
      const dimension = column.role === 'dimension';
      return new Intl.NumberFormat(LOCALE, {
        maximumFractionDigits: compact && !dimension ? 1 : 2,
        useGrouping: !dimension,
        ...(compact && !dimension ? { notation: 'compact' } : {}),
      }).format(value);
    }
  }
}

/** Valores seleccionados para la dimension de una columna (vacio si no hay seleccion). */
export function selectedValues(
  selections: readonly Selection[],
  column: QueryResultColumn,
): readonly ScalarValue[] {
  if (column.role !== 'dimension') return [];
  return selections.find((selection) => selection.dimensionId === column.fieldId)?.values ?? [];
}

export const isNumericColumn = (column: QueryResultColumn): boolean =>
  column.role !== 'dimension' || column.dataType === 'integer' || column.dataType === 'decimal';
